import React, { useState } from "react";
import {
  TableContainer,
  Table,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
  Theme,
  TablePagination as MuiTablePagination,
  TablePaginationProps,
  TableSortLabel,
  Checkbox,
} from "@material-ui/core";
import styled from "styled-components";

const useStyles = makeStyles((theme: Theme) => ({
  table: {
    background: "var(--backgroundContent)",
    "& .MuiTableRow-head": {
      borderBottom: "1px solid var(--grayLv4)",
    },
    "& thead th": {
      background: "var(--backgroundContent)",
      fontWeight: "700",
      fontSize: "1.2rem",
      textAlign: "left",
    },
    "& tbody td": {
      textAlign: "left",
      fontWeight: "400",
      fontSize: "1.2rem",
    },
    "& tbody tr": {
      transition: "all 0.2s",
    },
    "& .MuiTableCell-root": {
      padding: "8px 16px",
    },
  },
  root: {
    background: "var(--white)",
    borderRadius: "20px",
    "& .MuiTableRow-head": {
      height: "56px",
    },
    "& .MuiTableCell-head": {
      fontSize: "1.2rem",
      fontWeight: "700",
      color: "var(--colorGray)",
    },
    "& .MuiTableCell-root": {
      borderBottom: "none",
    },
    "& .MuiTableCell-root:first-child": {
      borderRadius: "10px 0 0 10px",
    },
    "& .MuiTableCell-root:last-child": {
      borderRadius: "0 10px 10px 0",
    },
  },
  checkbox: {
    "&.MuiCheckbox-colorSecondary.Mui-checked": {
      color: "var(--orange)",
    },
    "& .MuiSvgIcon-root": {
      width: "1.4rem",
    },
  },
  pagination: {
    background: "var(--backgroundContent)",
    "&.MuiTablePagination-root": {
      overflow: "hidden",
    },
    "& .MuiTablePagination-caption": {
      fontSize: "1rem",
    },
    "& .MuiTablePagination-input": {
      fontSize: "1.2rem",
    },
    "& .MuiSelect-select.MuiSelect-select": {
      borderRadius: "4px",
    },
    "& .MuiSelect-icon": {
      right: "4px",
    },
  },
}));

interface TablePaginationOwnProps extends TablePaginationProps<any> {
  component: string;
}
const TablePagination = styled(MuiTablePagination)<TablePaginationOwnProps>``;

type Order = "asc" | "desc";

interface AllowedActions {
  isAllow: boolean;
  edit: boolean;
  delete: boolean;
  activate: boolean;
  inactivate: boolean;
  confirm: boolean;
  renewCredential: boolean;
  cancel: boolean;
}

const allowedActions = {
  active: {
    isAllow: true,
    edit: true,
    delete: true,
    activate: false,
    inactivate: true,
    confirm: false,
    cancel: false,
    renewCredential: false,
  },
  inactivate: {
    isAllow: true,
    edit: true,
    delete: true,
    activate: true,
    inactivate: false,
    confirm: false,
    cancel: false,
    renewCredential: false,
  },
  delete: {
    isAllow: true,
    edit: true,
    delete: false,
    activate: true,
    inactivate: false,
    confirm: false,
    cancel: false,
    renewCredential: false,
  },
  pending: {
    isAllow: true,
    edit: false,
    delete: false,
    activate: false,
    inactivate: false,
    confirm: true,
    cancel: true,
    renewCredential: false,
  },
  renewCredentials: {
    isAllow: true,
    edit: false,
    delete: false,
    activate: false,
    inactivate: false,
    confirm: false,
    cancel: false,
    renewCredential: true,
  },
};

interface UseTableProps {
  records: any;
  headCells: any;
  filterFn: any;
  config?: {
    checkbox?: boolean;
    actions?: boolean;
  };
  setPagination?: any;
}

export default function useTable({
  records,
  headCells,
  filterFn,
  config,
  setPagination,
}: UseTableProps) {
  const refTable = React.createRef();
  const classes = useStyles();
  const pages = [25, 50, 100];
  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState();
  const [actions, setActions] = useState({} as AllowedActions);
  //TODO: LOG QUANTIDADE
  // TABLE CONTAINER
  // ------------------------------------------------------------

  const TblContainer = (props?: any) => (
    <TableContainer style={{ marginTop: "24px", ...props.style }}>
      <div style={{ overflow: "auto", maxHeight: "480px" }}>
        <Table innerRef={refTable} id="tableDownload" className={classes.table}>
          {props.children}
        </Table>
      </div>
    </TableContainer>
  );

  // TABLE HEAD
  // ------------------------------------------------------------
  interface TblHeadProps {
    numSelected: number;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    rowCount: number;
  }

  const TblHead = ({
    numSelected,
    rowCount,
    onSelectAllClick,
  }: TblHeadProps) => {
    const handleSortRequest = (cellId: any) => {
      const isAsc = orderBy === cellId && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(cellId);
      setPage(0);
    };

    return (
      <TableHead className={classes.root}>
        <TableRow>
          {config?.checkbox && (
            <TableCell padding="checkbox">
              <Checkbox
                className={classes.checkbox}
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                inputProps={{ "aria-label": "select all desserts" }}
              />
            </TableCell>
          )}
          {headCells.map((headCell: any) => (
            <TableCell
              key={headCell.id}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              {headCell.disableSorting ? (
                headCell.label
              ) : (
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={() => {
                    handleSortRequest(headCell.id);
                  }}
                >
                  {headCell.label}
                </TableSortLabel>
              )}
            </TableCell>
          ))}
          {config?.actions && <TableCell align="right"></TableCell>}
        </TableRow>
      </TableHead>
    );
  };

  // TABLE PAGINATION
  // ------------------------------------------------------------
  const handleChangePage = (event: any, newPage: any) => {
    // const OFFSET = ((newPage * rowsPerPage) - 1);
    // const LIMIT = newPage > page ? (newPage + 1) * rowsPerPage : page * rowsPerPage;
    // const OFFSET = ((newPage * rowsPerPage) - 1);
    // const LIMIT = rowsPerPage;
    setPage(newPage);
    // setPagination(OFFSET, LIMIT);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const TblPagination = () => {
    return (
      <TablePagination
        component="div"
        className={classes.pagination}
        labelRowsPerPage="Linhas por página: "
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={pages}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        count={filterFn.fn(records).length}
      />
    );
  };

  function stableSort(array: any, comparator: any) {
    const stabilizedThis = array?.map((el: any, index: any) => [el, index]);
    stabilizedThis.sort((a: any, b: any) => {
      const order = comparator(a[0], b[0]);

      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    // const eqNull = stabilizedThis.filter((el: any) => el[0].DHFIM === null);
    // const notEqNull = stabilizedThis.filter((el: any) => el[0].DHFIM !== null);
    // console.log(eqNull[0]);
    return stabilizedThis.map((el: any) => el[0]);
  }

  function getComparator(order: any, orderBy: any) {
    return order === "desc"
      ? (a: any, b: any) => descendingComparator(a, b, orderBy)
      : (a: any, b: any) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a: any, b: any, orderBy: any) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  const recordsAfterPagingAndSorting = () => {
    return stableSort(
      filterFn.fn(records),
      getComparator(order, orderBy)
    ).slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  };

  return {
    refTable,
    TblContainer,
    TblHead,
    TblPagination,
    recordsAfterPagingAndSorting,
    actions,
    setActions,
    setPage,
    allowedActions,
  };
}
